import React, { useState, useEffect, useMemo } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { ref, onValue, push, set, remove } from "firebase/database";
import useDebounce from "./hooks/useDebounce";

// Components
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import TaskCard from "./components/TaskCard";
import TaskModal from "./components/TaskModal";
import Stats from "./components/Stats";
import EmptyState from "./components/EmptyState";
import Auth from "./components/Auth";
import ProfilePage from "./components/ProfilePage";
import AdminDashboard from "./components/AdminDashboard";

export default function App() {
  // --- Core State ---
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState("dashboard");
  const [profilePic, setProfilePic] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  // --- UI State ---
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("theme_db") === "dark"
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // --- Data State ---
  const [tasks, setTasks] = useState([]);
  const [editingTask, setEditingTask] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const debouncedSearch = useDebounce(searchTerm, 400);

  // --- 1. Theme Sync ---
  useEffect(() => {
    localStorage.setItem("theme_db", darkMode ? "dark" : "light");
    document.documentElement.classList.toggle("dark", darkMode);
  }, [darkMode]);

  // --- 2. Unified Auth & Profile Listener ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setIsAdmin(currentUser.email === "admin@tasktracker.com");

        // Sync Profile Data
        const profileRef = ref(db, `users/${currentUser.uid}/profile`);
        onValue(
          profileRef,
          (snapshot) => {
            const profileData = snapshot.val();
            if (profileData) {
              setProfilePic(profileData.photo || currentUser.photoURL);
              setDisplayName(
                profileData.displayName || currentUser.email.split("@")[0]
              );
            } else {
              // Fallback for new users so they aren't stuck loading
              setDisplayName(currentUser.email.split("@")[0]);
            }
            // ONCE WE HAVE ATTEMPTED TO GET PROFILE, STOP LOADING
            setLoading(false);
          },
          (error) => {
            console.error("Profile Fetch Error:", error);
            setLoading(false);
          }
        );
      } else {
        // No user logged in
        setUser(null);
        setIsAdmin(false);
        setDisplayName("");
        setProfilePic(null);
        setLoading(false); // STOP LOADING TO SHOW AUTH SCREEN
      }
    });

    return () => unsubscribe();
  }, []);

  // --- 3. Tasks Realtime Sync ---
  useEffect(() => {
    if (!user || isAdmin) {
      setTasks([]);
      return;
    }
    const tasksRef = ref(db, `users/${user.uid}/tasks`);
    const unsubscribe = onValue(tasksRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const taskList = Object.entries(data).map(([id, value]) => ({
          id,
          ...value,
        }));
        setTasks(taskList);
      } else {
        setTasks([]);
      }
    });
    return () => unsubscribe();
  }, [user, isAdmin]);

  // --- 4. Task Action Handlers ---
  const handleSaveTask = async (taskData) => {
    try {
      const id = editingTask
        ? editingTask.id
        : push(ref(db, `users/${user.uid}/tasks`)).key;
      await set(ref(db, `users/${user.uid}/tasks/${id}`), {
        ...taskData,
        lastUpdated: new Date().toISOString(),
      });
      setIsModalOpen(false);
      setEditingTask(null);
    } catch (error) {
      console.error("Database Error:", error);
    }
  };

  const handleDeleteTask = async (id) => {
    await remove(ref(db, `users/${user.uid}/tasks/${id}`));
  };

  // --- 5. Filtering Logic ---
  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      const matchesStatus = statusFilter === "All" || t.status === statusFilter;
      const matchesSearch = t.title
        ?.toLowerCase()
        .includes(debouncedSearch.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  }, [tasks, debouncedSearch, statusFilter]);

  // --- 6. LOADING SCREEN (Only shown during handshake) ---
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-slate-400 font-bold animate-pulse uppercase text-[10px] tracking-widest">
          Establishing Secure Connection...
        </p>
      </div>
    );
  }

  // --- 7. AUTH SCREEN (Show first if not logged in) ---
  if (!user) return <Auth />;

  // --- 8. ADMIN VIEW ---
  if (isAdmin) {
    return (
      <AdminDashboard
        onLogout={() => signOut(auth)}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
      />
    );
  }

  // --- 9. REGULAR USER VIEW ---
  return (
    <div className="h-screen flex overflow-hidden bg-slate-50 dark:bg-slate-950">
      <Sidebar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        onLogout={() => signOut(auth)}
        setView={setCurrentView}
        userProfilePic={profilePic}
        displayName={displayName}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar
          user={user}
          displayName={displayName}
          userProfilePic={profilePic}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          searchTerm={searchTerm}
          onSearch={setSearchTerm}
          statusFilter={statusFilter}
          onFilterStatus={setStatusFilter}
          isSidebarOpen={isSidebarOpen}
          toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        />

        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="p-4 md:p-10 max-w-7xl mx-auto w-full pb-20">
            {currentView === "dashboard" ? (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <Stats tasks={tasks} />
                <div className="flex justify-center py-10">
                  <button
                    onClick={() => {
                      setEditingTask(null);
                      setIsModalOpen(true);
                    }}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-10 py-5 rounded-[2.5rem] font-black shadow-2xl active:scale-95 transition-all flex items-center gap-3"
                  >
                    <i className="fas fa-plus-circle text-xl"></i>
                    <span>Create New Task</span>
                  </button>
                </div>

                {filteredTasks.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredTasks.map((task) => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onEdit={(t) => {
                          setEditingTask(t);
                          setIsModalOpen(true);
                        }}
                        onDelete={handleDeleteTask}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyState />
                )}
              </div>
            ) : (
              <ProfilePage
                user={user}
                displayName={displayName}
                profilePic={profilePic}
                setProfilePic={setProfilePic}
                setView={setCurrentView}
              />
            )}
          </div>
        </main>
      </div>

      {isModalOpen && (
        <TaskModal
          editingTask={editingTask}
          onSave={handleSaveTask}
          onClose={() => {
            setIsModalOpen(false);
            setEditingTask(null);
          }}
        />
      )}
    </div>
  );
}
